//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  CampaignType,
  ContentLibraryContentGroup,
  ContentLibraryFilter,
  ContentLibrarySortType,
  ContentLibrarySourcesGroups,
  ContentLibraryV2RequestBody,
  IContentLibraryFiltersOptions,
  IContentLibraryItemParams,
  IContentLibrarySourcesOptions,
  NetworkType,
} from '@apis/content-library/contentLibraryApiModel';
import { MediaLibrarySubPages } from '@media-library/constants/mediaLibraryConstants';

const REQUEST_ITEM_LIMIT = 50;

export const EmptySources: IContentLibrarySourcesOptions = {
  accountMentionIds: [],
  brandFeedPost: undefined,
  brandStoryPost: undefined,
  brandTikTokPosts: [],
  collectionId: undefined,
  contentGroup: undefined,
  curalateUserIds: [],
  pinterestUserIds: [],
  sourcesGroups: [],
  tiktokMentionedUserIds: [],
  trackedBusinessUserIds: [],
  trackedHashtags: [],
  trackedUserIds: [],
} as const;

export const InitialSourceGroups: Record<
  MediaLibrarySubPages,
  IContentLibrarySourcesOptions
> = {
  ALL_MEDIA: {
    contentGroup: ContentLibraryContentGroup.NOTHING,
    sourcesGroups: [ContentLibrarySourcesGroups.ALL_MEDIA],
  },
  TEAM_MEDIA: {
    contentGroup: ContentLibraryContentGroup.NOTHING,
    sourcesGroups: [ContentLibrarySourcesGroups.TEAM_MEDIA],
  },
  MY_MEDIA: {
    curalateUserIds: [],
    contentGroup: ContentLibraryContentGroup.NOTHING,
  },
} as const;

export const EmptyFilters: IContentLibraryFiltersOptions = {
  campaignTypes: undefined,
  campaignSubTypes: undefined,
  dateRange: undefined,
  distribution: undefined,
  // PD-301894: Remove excludeTargetedRetailerCampaigns and excludeNonTargetedRetailerCampaigns
  excludeNonTargetedRetailerCampaignItems: undefined,
  excludeTargetedRetailerCampaignItems: undefined,
  hasLabels: undefined,
  hasProducts: undefined,
  igHashtags: undefined,
  igUsernames: undefined,
  instagramMentionedUsernames: undefined,
  isDistributed: undefined,
  labels: undefined,
  mediaItemTypes: undefined,
  networks: undefined,
  orientation: undefined,
  productIds: undefined,
  rightsStatuses: undefined,
  searchText: undefined,
  semanticTags: undefined,
  spamFiltering: undefined,
} as const;

export const InitialMediaLibraryItemParams: IContentLibraryItemParams = {
  filters: EmptyFilters,
  hydrateWithDeetsItems: true,
  limit: REQUEST_ITEM_LIMIT,
  offset: 0,
  orderBy: ContentLibrarySortType.TIME,
} as const;

const UPLOADED_CONTENT_FILTER: ContentLibraryFilter = {
  '@type': 'network',
  networkTypes: [NetworkType.MEDIA_IMPORT],
};

// All Media content that excludes organically sourced content (e.g. tracked users, tracked hashtags).
// Content that has been uploaded through media import, or is part of campaigns
const getAllMediaFilterParamsWithoutOrganicContent = (
  isCreatorCampaignsEnabled: boolean,
): ContentLibraryV2RequestBody => {
  return {
    query: {
      '@type': 'or',
      filters: [
        { ...UPLOADED_CONTENT_FILTER },
        {
          '@type': 'campaignType',
          types: isCreatorCampaignsEnabled
            ? [CampaignType.Sampling, CampaignType.Creator]
            : [CampaignType.Sampling],
        },
      ],
    },
  };
};

// Team Media content - Content that has been uploaded through media import
const teamMediaFilterParamsWithoutOrganicContent: ContentLibraryV2RequestBody =
  {
    query: { ...UPLOADED_CONTENT_FILTER },
  };

// My Media content - Content that has been uploaded through media import by the current user
const getMyMediaFilterParamsWithoutOrganicContent = (
  userId: string,
): ContentLibraryV2RequestBody => {
  return {
    query: {
      '@type': 'userId',
      userIds: [userId],
      networkType: NetworkType.MEDIA_IMPORT,
    },
  };
};

// Gets the initial filter params based on the subpage and whether creator campaigns is enabled
export const getSamplingOnlyInitialFilterParams = (
  subPage: MediaLibrarySubPages,
  isCreatorCampaignsEnabled: boolean,
  userId: string,
): ContentLibraryV2RequestBody => {
  switch (subPage) {
    case 'ALL_MEDIA':
      return getAllMediaFilterParamsWithoutOrganicContent(
        isCreatorCampaignsEnabled,
      );
    case 'TEAM_MEDIA':
      return teamMediaFilterParamsWithoutOrganicContent;
    case 'MY_MEDIA':
      return getMyMediaFilterParamsWithoutOrganicContent(userId);
    default: {
      const _exhaustive: never = subPage;
      throw new Error(`Unhandled subpage: ${_exhaustive}`);
    }
  }
};
